import { FC } from 'react';
import { withStyles, Flag, formatAmount, VirtualAccount } from 'vopay';
import { CopyButton, IconButton } from 'UI';
import { useTranslation } from 'react-i18next';
import { Balance } from '@/hooks/useApi/useAccountBalances';
import { AccountType } from '../AccountSummaryPage';
import AccountTypeBadge from '../AccountUI/AccountTypeBadge';
import styles from './BalanceListItem.module.scss';
import { useClientData } from '@/providers/ClientData.provider';

const cn = withStyles(styles);

type BalanceListItemProps = {
  accountType: AccountType;
  balance: Balance;
  selected: boolean;
  virtualAccount: VirtualAccount | null;
  onSimulateInboundTransfer: (virtualAccountID: string, currency: string) => void;
};

const BalanceListItem: FC<BalanceListItemProps> = ({
  accountType,
  balance,
  selected,
  virtualAccount,
  onSimulateInboundTransfer,
}) => {
  const simulateInboundTransferEnabled = import.meta.env.VITE_SIMULATE_INBOUND_TRANSFERS_ENABLED === 'true';
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances' });

  const {
    response: { VirtualAccountsEnabled },
  } = useClientData();

  const account = virtualAccount
    ? [
        virtualAccount.IBAN,
        virtualAccount.BicSwift,
        virtualAccount.FinancialInstitutionNumber,
        virtualAccount.BranchTransitNumber,
        virtualAccount.ABARoutingNumber,
        virtualAccount.AccountNumber,
      ]
        .filter((value) => Boolean(value))
        .join('-')
    : '';

  return (
    <div className={cn('balanceListItem', selected && 'selected')}>
      <Flag flag={balance.Currency} width="40px" />

      <div>
        <div style={{ paddingBottom: '8px' }}>
          <AccountTypeBadge status={accountType} />
        </div>

        <div className={cn('currencyInfo')}>
          <div className={cn('currency')}>{balance.Currency}</div>

          {virtualAccount && (
            <div className={cn('accountInfo')}>
              <span>{account}</span>
              <CopyButton content={account} size={'16px'} />
            </div>
          )}
        </div>

        <div className={cn('grid')}>
          <div className={cn('gridLabel')}>{t('accountBalance')}</div>
          <div className={cn(balance.AccountBalance < 0 && 'negative')}>{formatAmount(balance.AccountBalance)}</div>

          <div className={cn('gridLabel')}>{t('availableBalance')}</div>
          <div className={cn(balance.AccountBalance < 0 && 'negative')}>{formatAmount(balance.AvailableFunds)}</div>
        </div>
      </div>

      {VirtualAccountsEnabled &&
        simulateInboundTransferEnabled &&
        virtualAccount &&
        virtualAccount.Status === 'Active' && (
          <IconButton
            style={{
              marginLeft: 'auto',
            }}
            icon="arrow_downward"
            aria-label="simulate inbound transfer"
            size="small"
            onClick={() => {
              onSimulateInboundTransfer(virtualAccount.VirtualAccountID, balance.Currency);
            }}
          />
        )}
    </div>
  );
};

export default BalanceListItem;
